import { type TextareaHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { startCase } from 'lodash';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  isLoading?: boolean;
  appendIcon?: any;
  indeterminate?: boolean;
  textareaClassName?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, textareaClassName, isLoading, appendIcon, indeterminate, 'aria-invalid': ariaInvalid, ...props },
    ref,
  ) => {
    const tCommon = useTranslations('common');

    return (
      <div
        className={cn(
          'flex items-start rounded-lg border-0 shadow-none ring-1 ring-ui-bevel transition duration-150 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ui-separator focus-within:ring-offset-0 focus-within:duration-150 disabled:cursor-not-allowed disabled:opacity-50 has-aria-invalid:ring-red-500! has-aria-invalid:focus-within:ring-red-500!',
          indeterminate && 'bg-status-warning/25! ring-status-warning/50 dark:ring-status-warning-dark/50',
          className,
          props.disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <textarea
          aria-invalid={ariaInvalid}
          className={cn(
            'flex min-h-15 w-full rounded-xl bg-transparent p-1.5 text-xs transition duration-150 focus-visible:outline-hidden disabled:cursor-not-allowed',
            textareaClassName,
            indeterminate
              ? 'placeholder:italic placeholder:text-status-warning'
              : 'placeholder:text-text-gray-alternate',
          )}
          ref={ref}
          {...props}
          placeholder={
            indeterminate
              ? tCommon('multipleValuesSelected', {
                  field: props.name ? startCase(props.name.replace(/_/g, ' ')) : 'none',
                })
              : props.placeholder
          }
        />
        {isLoading ? <Loader2 className="m-1 icon-4 animate-spin opacity-50" strokeWidth={2.5} /> : appendIcon}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

export { Textarea };
